import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Bell, Send, Smartphone, Tag, Calendar, Zap,
  Megaphone, Clock, ChevronDown, ChevronUp, Users,
  Wifi, AlertTriangle, Settings
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PushAutomations } from '@/components/admin/PushAutomations';
import { SmartCoachSettings } from '@/components/admin/SmartCoachSettings';
import { PushABTests } from '@/components/admin/PushABTests';
import PushBroadcastPanel from '@/components/admin/PushBroadcastPanel';
import BroadcastHistory from '@/components/admin/BroadcastHistory';
import UpsellPushSettings from '@/components/admin/UpsellPushSettings';
import PushReminderConfig from '@/components/admin/PushReminderConfig';
import MobileNotificationTemplates from '@/components/admin/MobileNotificationTemplates';
import { WinBackFlow } from '@/components/admin/WinBackFlow';
import PushAnalytics from '@/components/admin/PushAnalytics';
import TrialFollowupConversionWidget from '@/components/admin/TrialFollowupConversionWidget';
import MarketingCampaignComposer from '@/components/admin/MarketingCampaignComposer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Alex direkt-Kontakt (Sicherheitshinweis) – 0041 79 696 98 99
const ALEX_WA_NUMBER_DISPLAY = '+41 79 696 98 99';
const ALEX_WA_LINK = 'https://wa.me/41796969899?text=' + encodeURIComponent(
  'Hallo StrongFit, ich möchte etwas Verdächtiges melden (Termin ohne Grund abgesagt / komische Mail oder WhatsApp).'
);
const ALEX_EMAIL = 'info@strongfit.ch';

const SECURITY_NOTICE_SUBJECT = '🔒 Wichtiger Sicherheitshinweis von StrongFit – bitte kurz lesen';

const SECURITY_NOTICE_HTML = `<div style="background:#0a0a0a;padding:32px 16px;font-family:Arial,Helvetica,sans-serif;">
  <div style="max-width:560px;margin:0 auto;background:#1a1a1a;border:1px solid #2a2a2a;border-radius:14px;overflow:hidden;">
    <div style="background-color:#F59E0B;background:#F59E0B;background-image:linear-gradient(135deg,#FFD700 0%,#F59E0B 100%);padding:22px 28px;">
      <h1 style="margin:0;color:#0a0a0a;font-size:22px;font-weight:800;">STRONGFIT · Sicherheitshinweis</h1>
    </div>
    <div style="padding:28px;color:#e5e5e5;">
      <p style="font-size:16px;line-height:1.6;margin:0 0 16px;">Hallo 👋</p>
      <p style="font-size:16px;line-height:1.6;margin:0 0 16px;">
        Kurz und wichtig: <strong style="color:#FFD700;">Melde dich bitte SOFORT bei uns</strong>, wenn
      </p>
      <ul style="font-size:15px;line-height:1.7;margin:0 0 20px;padding-left:20px;">
        <li>ein Training <strong>ohne Grund</strong> abgesagt wurde – weder von dir noch von uns,</li>
        <li>du eine <strong>ungewöhnliche oder komische E-Mail bzw. WhatsApp</strong> in unserem Namen bekommst.</li>
      </ul>
      <p style="font-size:15px;line-height:1.6;margin:0 0 20px;color:#a0a0a0;">
        So können wir sofort reagieren und dich schützen. Lieber einmal zu viel melden als zu wenig – danke dir! 🙏
      </p>
      <p style="font-size:15px;line-height:1.6;margin:0 0 24px;color:#e5e5e5;">
        Und für <strong style="color:#FFD700;">Änderungen, Anpassungen oder Fehler</strong> in der App: melde dich bitte ebenfalls direkt bei Alex – am einfachsten mit einem kurzen <strong>Screenshot</strong> oder einer <strong>Sprachnachricht</strong>.
      </p>
      <div style="text-align:center;margin:0 0 14px;">
        <a href="${ALEX_WA_LINK}" style="display:inline-block;background:#25D366;color:#ffffff;text-decoration:none;font-weight:700;font-size:16px;padding:14px 26px;border-radius:10px;">
          📱 Jetzt direkt Alex auf WhatsApp melden
        </a>
      </div>
      <p style="text-align:center;font-size:13px;color:#a0a0a0;margin:0 0 22px;">
        oder ${ALEX_WA_NUMBER_DISPLAY} · <a href="mailto:${ALEX_EMAIL}" style="color:#FFD700;">E-Mail schreiben</a>
      </p>
      <p style="font-size:14px;line-height:1.6;margin:0;color:#e5e5e5;">Sportliche Grüsse<br/><strong>Dein StrongFit-Team</strong></p>
    </div>
  </div>
</div>`;

const SECURITY_NOTICE_WA_TEXT = `🔒 StrongFit – wichtiger Hinweis

Melde dich bitte SOFORT bei uns, wenn:
• ein Training ohne Grund abgesagt wurde (weder von dir noch von uns)
• du eine komische/ungewöhnliche E-Mail oder WhatsApp in unserem Namen bekommst

Für Änderungen, Anpassungen oder Fehler in der App: bitte ebenfalls direkt bei Alex melden – am einfachsten per Screenshot oder Sprachnachricht.

👉 Direkt an Alex: ${ALEX_WA_LINK}
oder ${ALEX_WA_NUMBER_DISPLAY}

Danke & sportliche Grüsse
Dein StrongFit-Team`;

const SOFTWARE_RELEASE_SUBJECT = 'Deine neue StrongFit-App ist da! 🚀';

const _releaseFeature = (icon, title, text) => `
      <tr>
        <td style="vertical-align:top;padding:10px 12px 10px 0;font-size:22px;">${icon}</td>
        <td style="vertical-align:top;padding:10px 0;">
          <p style="margin:0 0 3px;color:#FFD700;font-size:15px;font-weight:700;">${title}</p>
          <p style="margin:0;color:#a0a0a0;font-size:14px;line-height:1.55;">${text}</p>
        </td>
      </tr>`;

const SOFTWARE_RELEASE_HTML = `<div style="background:#0a0a0a;padding:32px 16px;font-family:Arial,Helvetica,sans-serif;">
  <div style="max-width:560px;margin:0 auto;background:#1a1a1a;border:1px solid #2a2a2a;border-radius:14px;overflow:hidden;">
    <div style="background-color:#F59E0B;background:#F59E0B;background-image:linear-gradient(135deg,#FFD700 0%,#F59E0B 100%);padding:22px 28px;">
      <h1 style="margin:0;color:#0a0a0a;font-size:22px;font-weight:800;">STRONGFIT · Deine neue App 🚀</h1>
    </div>
    <div style="padding:28px;color:#e5e5e5;">
      <p style="font-size:16px;line-height:1.6;margin:0 0 16px;">Hallo 👋</p>
      <p style="font-size:16px;line-height:1.6;margin:0 0 22px;">
        Wir haben unsere <strong style="color:#FFD700;">StrongFit-App</strong> komplett aufgewertet – damit du dein Training,
        deine Ernährung und alles Rund­herum ganz easy im Griff hast. Das ist neu für dich:
      </p>
      <table role="presentation" cellpadding="0" cellspacing="0" style="width:100%;border-collapse:collapse;margin:0 0 8px;">
        ${_releaseFeature('📸', 'Ernährung per Foto tracken', 'Einfach dein Essen fotografieren – die App erkennt Kalorien &amp; Makros automatisch. Kein mühsames Eintippen mehr.')}
        ${_releaseFeature('⏱️', 'Warteliste für Termine', 'Slot ausgebucht? Setz dich auf die Warteliste und rücke automatisch nach, sobald etwas frei wird.')}
        ${_releaseFeature('💳', 'Kosten &amp; Abos jederzeit im Griff', 'Dein Abo, dein Guthaben und alle Zahlungen übersichtlich an einem Ort – volle Transparenz, keine Überraschungen.')}
        ${_releaseFeature('📄', 'Alles klar dokumentiert', 'Rechnungen, Trainings und Verlauf sauber dokumentiert und jederzeit für dich abrufbar.')}
        ${_releaseFeature('📅', 'Termine selbst buchen &amp; verschieben', 'Buche, verschiebe oder storniere deine Trainings bequem selbst – rund um die Uhr.')}
        ${_releaseFeature('⭐', 'Punkte &amp; Guthaben', 'Behalte deine Punkte und dein Guthaben im Blick und setze sie flexibel ein.')}
        ${_releaseFeature('🏋️', 'Trainingspläne', 'Deine Pläne und Fortschritte immer griffbereit – für maximale Ergebnisse.')}
      </table>
      <div style="text-align:center;margin:24px 0 14px;">
        <a href="https://app.strongfit.ch" style="display:inline-block;background:#F59E0B;color:#0a0a0a;text-decoration:none;font-weight:700;font-size:16px;padding:14px 28px;border-radius:10px;">
          Jetzt App öffnen
        </a>
      </div>
      <p style="text-align:center;font-size:13px;color:#a0a0a0;margin:0 0 22px;">
        Fragen oder Feedback? Melde dich direkt bei Alex – am einfachsten per Screenshot oder Sprachnachricht.
      </p>
      <p style="font-size:14px;line-height:1.6;margin:0;color:#e5e5e5;">Sportliche Grüsse<br/><strong>Dein StrongFit-Team</strong></p>
    </div>
  </div>
</div>`;


// ---- Vorlage: StrongFit Rapperswil-Jona Eröffnung ----
const RAPPERSWIL_OPENING_SUBJECT = 'Endlich ist es soweit – StrongFit Rapperswil-Jona eröffnet 🥂';
const RAPPERSWIL_OPENING_HTML = `<div style="background:#0a0a0a;padding:32px 16px;font-family:Arial,Helvetica,sans-serif;">
  <div style="max-width:560px;margin:0 auto;background:#141414;border:1px solid #2a2a2a;border-radius:16px;overflow:hidden;">
    <div style="padding:36px 32px 24px;text-align:center;border-bottom:1px solid #232323;">
      <div style="color:#F59E0B;font-size:12px;font-weight:700;letter-spacing:3px;text-transform:uppercase;margin:0 0 14px;">StrongFit · Rapperswil-Jona</div>
      <div style="color:#ffffff;font-size:30px;font-weight:800;line-height:1.15;margin:0 0 10px;">Endlich ist es soweit</div>
      <div style="color:#c9c9c9;font-size:16px;line-height:1.55;margin:0;">Unser neues Studio öffnet seine Türen &ndash; und du bist von Anfang an dabei.</div>
    </div>
    <div style="padding:32px;color:#e5e5e5;">
      <p style="font-size:16px;line-height:1.7;margin:0 0 16px;color:#e5e5e5;">Hallo 👋</p>
      <p style="font-size:16px;line-height:1.7;margin:0 0 26px;color:#d0d0d0;">
        Monatelang haben wir gebaut, geplant und geschraubt &mdash; jetzt ist unser neues Studio in
        <strong style="color:#F59E0B;">Rapperswil-Jona</strong> bereit für dich. Komm vorbei, staune und trainiere gleich mit.
      </p>
      <div style="background:#10231a;border:1px solid #1f4536;border-left:4px solid #22c55e;border-radius:12px;padding:20px 22px;margin:0 0 18px;">
        <div style="color:#4ade80;font-size:17px;font-weight:800;margin:0 0 14px;">🥂 Eröffnungs-Apéro</div>
        <div style="color:#e8f6ee;font-size:15px;line-height:1.75;">
          <div style="margin:0 0 7px;">🗓️&nbsp;&nbsp;Sonntag, 27. September · 11:00–16:00 Uhr</div>
          <div style="margin:0 0 7px;">📍&nbsp;&nbsp;Musterstrasse 12, 8640 Rapperswil-Jona</div>
          <div style="margin:0;">🅿️&nbsp;&nbsp;Gratis Parkplätze vorhanden</div>
        </div>
      </div>
      <div style="background:#2a1512;border:1px solid #5b2a20;border-radius:10px;padding:13px 16px;margin:0 0 28px;color:#fca5a5;font-size:14px;line-height:1.55;">
        ⏳&nbsp;&nbsp;Nur noch begrenzte Plätze &mdash; melde dich bis <strong style="color:#fecaca;">25. September</strong> an.
      </div>
      <div style="text-align:center;margin:0 0 12px;">
        <a href="https://app.strongfit.ch" style="display:inline-block;background:#F59E0B;color:#0a0a0a;text-decoration:none;font-weight:800;font-size:16px;padding:15px 34px;border-radius:10px;">Jetzt Probetraining buchen →</a>
      </div>
      <div style="text-align:center;margin:0 0 28px;">
        <a href="https://app.strongfit.ch" style="display:inline-block;background:transparent;color:#F59E0B;text-decoration:none;font-weight:700;font-size:14px;padding:11px 22px;border:1px solid #4a3a10;border-radius:10px;">Mehr über Mitgliedschaften erfahren</a>
      </div>
      <div style="border-top:1px solid #232323;padding-top:20px;">
        <p style="font-size:15px;line-height:1.6;margin:0;color:#e5e5e5;">Wir freuen uns auf dich!<br/><strong style="color:#ffffff;">Dein StrongFit-Team</strong></p>
      </div>
    </div>
  </div>
</div>`;

// ---- Vorlage: 10% Rabatt lebenslang für Vorregistrierte ----
const PREREG_DISCOUNT_SUBJECT = 'Dein Willkommens-Geschenk: 10% Rabatt – ein Leben lang 🎉';
const PREREG_DISCOUNT_HTML = `<div style="background:#0a0a0a;padding:32px 16px;font-family:Arial,Helvetica,sans-serif;">
  <div style="max-width:560px;margin:0 auto;background:#141414;border:1px solid #2a2a2a;border-radius:16px;overflow:hidden;">
    <div style="padding:36px 32px 24px;text-align:center;border-bottom:1px solid #232323;">
      <div style="color:#F59E0B;font-size:12px;font-weight:700;letter-spacing:3px;text-transform:uppercase;margin:0 0 14px;">StrongFit · Nur für Vorregistrierte</div>
      <div style="color:#ffffff;font-size:27px;font-weight:800;line-height:1.18;margin:0 0 10px;">Danke, dass du von Anfang an dabei bist</div>
      <div style="color:#c9c9c9;font-size:16px;line-height:1.55;margin:0;">Als Dankeschön für deine Vorregistrierung haben wir etwas Besonderes für dich.</div>
    </div>
    <div style="padding:32px;color:#e5e5e5;">
      <p style="font-size:16px;line-height:1.7;margin:0 0 24px;color:#d0d0d0;">Hallo 👋<br/><br/>
        Du hast dich schon vor der Eröffnung von <strong style="color:#F59E0B;">StrongFit Rapperswil-Jona</strong> registriert &mdash; und genau das belohnen wir. Dein persönliches Willkommens-Geschenk:</p>
      <div style="background-image:linear-gradient(135deg,#FFD700 0%,#F59E0B 100%);background-color:#F59E0B;border-radius:16px;padding:28px 24px;text-align:center;margin:0 0 24px;">
        <div style="color:#0a0a0a;font-size:14px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;margin:0 0 6px;">Dein Vorteil</div>
        <div style="color:#0a0a0a;font-size:48px;font-weight:900;line-height:1;margin:0 0 6px;">10%</div>
        <div style="color:#0a0a0a;font-size:18px;font-weight:800;margin:0;">Rabatt &mdash; ein Leben lang</div>
      </div>
      <p style="font-size:15px;line-height:1.75;margin:0 0 26px;color:#d0d0d0;">
        Die <strong style="color:#ffffff;">10% gelten dauerhaft</strong> auf deine StrongFit-Mitgliedschaft &mdash; solange du dabei bist. Kein Aktionscode, keine Frist: Wir hinterlegen deinen Rabatt automatisch bei deiner Anmeldung.
      </p>
      <div style="text-align:center;margin:0 0 28px;">
        <a href="https://app.strongfit.ch" style="display:inline-block;background:#F59E0B;color:#0a0a0a;text-decoration:none;font-weight:800;font-size:16px;padding:15px 34px;border-radius:10px;">Jetzt Mitgliedschaft sichern →</a>
      </div>
      <div style="background:#161616;border:1px solid #2a2a2a;border-radius:12px;padding:16px 18px;margin:0 0 28px;color:#a8a8a8;font-size:13px;line-height:1.65;">
        💡&nbsp; Übrigens: Zur Eröffnung feiern wir mit einem Apéro am <strong style="color:#e5e5e5;">Sonntag, 27. September (11:00–16:00 Uhr)</strong> an der Musterstrasse 12, Rapperswil-Jona. Wir würden uns freuen, dich persönlich kennenzulernen!
      </div>
      <div style="border-top:1px solid #232323;padding-top:20px;">
        <p style="font-size:15px;line-height:1.6;margin:0;color:#e5e5e5;">Bis bald im Studio!<br/><strong style="color:#ffffff;">Dein StrongFit-Team</strong></p>
      </div>
    </div>
  </div>
</div>`;


const CampaignsPage = () => {
  const navigate = useNavigate();
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterHtml, setNewsletterHtml] = useState('');
  const [newsletterSendEmail, setNewsletterSendEmail] = useState(true);
  const [newsletterSendWhatsapp, setNewsletterSendWhatsapp] = useState(false);
  const [waTemplates, setWaTemplates] = useState([]);
  const [loadingWaTemplates, setLoadingWaTemplates] = useState(false);
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [recipientPreview, setRecipientPreview] = useState(null);
  const [nlAiPrompt, setNlAiPrompt] = useState('');
  const [nlAiGenerating, setNlAiGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [scheduledList, setScheduledList] = useState([]);

  const authHdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchScheduled = async () => {
    try {
      const res = await axios.get(`${API}/newsletters/scheduled`, authHdr());
      setScheduledList(res.data.scheduled || []);
    } catch (e) { /* still ok */ }
  };

  const handleAiDraft = async () => {
    if (!nlAiPrompt.trim()) { toast.error('Beschreibe zuerst, was erstellt werden soll'); return; }
    setNlAiGenerating(true);
    try {
      const res = await axios.post(`${API}/newsletters/ai-template`, { prompt: nlAiPrompt }, authHdr());
      if (res.data.email) {
        setNewsletterSubject(res.data.email.subject);
        setNewsletterHtml(res.data.email.html_body);
        setShowPreview(true);
      }
      if (res.data.whatsapp && res.data.whatsapp.body_text) {
        setWaAiDraft(res.data.whatsapp);
      }
      toast.success('✨ E-Mail + WhatsApp-Vorlage im StrongFit-Layout erstellt!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'KI-Erstellung fehlgeschlagen');
    } finally {
      setNlAiGenerating(false);
    }
  };

  const handleCreateWaTemplate = async () => {
    if (!waAiDraft || !waAiDraft.body_text) return;
    setCreatingWaTemplate(true);
    try {
      const components = [{
        type: 'BODY',
        text: waAiDraft.body_text,
        ...(waAiDraft.example_values && waAiDraft.example_values.length
          ? { example: { body_text: [waAiDraft.example_values] } } : {}),
      }];
      await axios.post(`${API}/whatsapp/templates/create`, {
        name: waAiDraft.suggested_name,
        category: waAiDraft.category || 'MARKETING',
        language: waAiDraft.language || 'de',
        components,
      }, authHdr());
      toast.success('WhatsApp-Vorlage bei Meta eingereicht – Genehmigung folgt i.d.R. innert Minuten bis Stunden.');
      setWaAiDraft(null);
      fetchWaTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anlegen bei Meta fehlgeschlagen');
    } finally {
      setCreatingWaTemplate(false);
    }
  };

  const fillWaVars = (text, values) =>
    (text || '').replace(/{{(\d+)}}/g, (_m, n) => (values[Number(n) - 1] || `{{${n}}}`));

  const handleTestSend = async () => {
    if (!newsletterSubject.trim() || !newsletterHtml.trim()) { toast.error('Betreff und Inhalt ausfüllen'); return; }
    setTestSending(true);
    try {
      const res = await axios.post(`${API}/newsletters/test-send`, {
        subject: newsletterSubject, html_body: newsletterHtml,
      }, authHdr());
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test-Versand fehlgeschlagen');
    } finally {
      setTestSending(false);
    }
  };

  const handleSchedule = async () => {
    if (!newsletterSubject.trim() || !newsletterHtml.trim()) { toast.error('Betreff und Inhalt ausfüllen'); return; }
    if (!scheduleAt) { toast.error('Wähle Datum und Uhrzeit'); return; }
    setScheduling(true);
    try {
      await axios.post(`${API}/newsletters/schedule`, {
        subject: newsletterSubject, html_body: newsletterHtml,
        scheduled_at: new Date(scheduleAt).toISOString(),
      }, authHdr());
      toast.success(`Newsletter geplant für ${new Date(scheduleAt).toLocaleString('de-CH')}`);
      setScheduleAt('');
      fetchScheduled();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Planung fehlgeschlagen');
    } finally {
      setScheduling(false);
    }
  };

  const handleCancelScheduled = async (id) => {
    try {
      await axios.delete(`${API}/newsletters/scheduled/${id}`, authHdr());
      toast.success('Geplanter Newsletter storniert');
      fetchScheduled();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Stornierung fehlgeschlagen');
    }
  };

  const [logs, setLogs] = useState([]);
  const fetchWaTemplates = async () => {
    try {
      setLoadingWaTemplates(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/whatsapp/templates?status=APPROVED&limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data?.data || [];
      setWaTemplates(list);
    } catch (error) {
      console.error('Fehler beim Laden der WhatsApp-Templates', error);
      toast.error('Fehler beim Laden der WhatsApp-Templates');
    } finally {
      setLoadingWaTemplates(false);
    }
  };

  const [logsLoading, setLogsLoading] = useState(true);

  // WhatsApp Template Newsletter
  const [waTemplateName, setWaTemplateName] = useState('');
  const [waLanguageCode, setWaLanguageCode] = useState('de');
  const [waBodyParams, setWaBodyParams] = useState(''); // Kommagetrennte Liste von Parametern
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [waRequiredParams, setWaRequiredParams] = useState(0);
  const [waVarValues, setWaVarValues] = useState([]); // Werte für {{1}},{{2}}… (Vorschau + Versand)
  const [waAiDraft, setWaAiDraft] = useState(null); // KI-generierte WhatsApp-Vorlage (noch nicht bei Meta)
  const [creatingWaTemplate, setCreatingWaTemplate] = useState(false);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/newsletters/logs?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(res.data || []);
    } catch (error) {
      console.error('Fehler beim Laden des Newsletter-Verlaufs', error);
      toast.error('Fehler beim Laden des Newsletter-Verlaufs');
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchWaTemplates();
    fetchScheduled();
    axios.get(`${API}/newsletters/recipients-preview`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(res => setRecipientPreview(res.data)).catch(() => {});
    // Entwurf aus dem CRM übernehmen (zentrale Sendestelle)
    try {
      const prefillRaw = localStorage.getItem('sf_marketing_prefill');
      if (prefillRaw) {
        const p = JSON.parse(prefillRaw);
        if (p?.subject) setNewsletterSubject(p.subject);
        if (p?.body) setNewsletterHtml(p.body);
        setShowPreview(true);
        localStorage.removeItem('sf_marketing_prefill');
        toast.info('📝 Entwurf aus CRM übernommen – bitte prüfen & senden.');
      }
    } catch { /* noop */ }
  }, []);

  const handleSendNewsletter = async () => {
    if (!newsletterSubject.trim() || !newsletterHtml.trim()) {
      toast.error('Betreff und Inhalt dürfen nicht leer sein');
      return;
    }
    if (!newsletterSendEmail && !newsletterSendWhatsapp) {
      toast.error('Bitte mindestens einen Kanal (Email oder WhatsApp) wählen');
      return;
    }

    try {
      setSendingNewsletter(true);
      const token = localStorage.getItem('token');
      const channels = [];
      if (newsletterSendEmail) channels.push('email');
      // WhatsApp-Newsletter per Template wird getrennt gehandhabt

      const res = await axios.post(
        `${API}/newsletters/send`,
        {
          subject: newsletterSubject,
          html_body: newsletterHtml,
          channels,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success(
        `E-Mail Newsletter gestartet (Email: ${res.data.sent_email})`
      );
      setNewsletterSubject('');
      setNewsletterHtml('');
    } catch (error) {
      console.error('Fehler beim Newsletter-Versand', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Newsletter-Versand');
    } finally {
      setSendingNewsletter(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Marketing</h1>
          <p className="text-gray-600 mt-1">
            Kampagnen an Leads, Newsletter-Abonnenten & Kunden – E-Mail, WhatsApp, Push und Automationen an einem Ort.
          </p>
        </div>
        <Button
          variant="outline"
          className="shrink-0 gap-2"
          data-testid="campaigns-marketing-settings-btn"
          onClick={() => navigate('/settings/ki?subtab=marketing')}
        >
          <Settings className="h-4 w-4" />
          Marketing-Einstellungen
        </Button>
      </div>

      <Tabs defaultValue="campaign-send" className="w-full">
        <TabsList className="flex flex-wrap h-auto gap-1 justify-start" data-testid="campaigns-tabs">
          <TabsTrigger value="campaign-send" data-testid="campaigns-tab-send">📣 Kampagne senden</TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="campaigns-tab-newsletter">📰 Newsletter (klassisch)</TabsTrigger>
          <TabsTrigger value="push" data-testid="campaigns-tab-push">🔔 Push-Nachrichten</TabsTrigger>
          <TabsTrigger value="automations" data-testid="campaigns-tab-automations">🔄 Automationen</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="campaigns-tab-analytics">📊 Analyse</TabsTrigger>
        </TabsList>

        <TabsContent value="campaign-send" className="space-y-6 mt-4">
          <MarketingCampaignComposer />
        </TabsContent>

        <TabsContent value="newsletter" className="space-y-6 mt-4">
      <div className="rounded-lg border border-teal-300 bg-teal-50 p-3 text-sm text-teal-900" data-testid="newsletter-unified-hint">
        💡 <strong>Neu:</strong> Der zentrale Kampagnen-Versand mit Zielgruppen-Auswahl (Leads / Newsletter / Kunden), E-Mail + WhatsApp und Platzhaltern ist jetzt im Tab <strong>„📣 Kampagne senden"</strong>. Dieser klassische Newsletter-Bereich bleibt für geplante Newsletter & erneuten Versand erhalten.
      </div>
      <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-white" data-testid="newsletter-ai-designer-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">✨ KI-Vorlagen-Designer (E-Mail + WhatsApp)</CardTitle>
          <CardDescription>
            Beschreibe dein Thema – die KI erstellt daraus eine <strong>E-Mail im StrongFit-Layout</strong> (schwarz/gold) UND einen passenden <strong>WhatsApp-Vorlagen-Text</strong>. Den WhatsApp-Entwurf kannst du danach unten mit einem Klick bei Meta anlegen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            data-testid="newsletter-ai-prompt"
            value={nlAiPrompt}
            onChange={(e) => setNlAiPrompt(e.target.value)}
            placeholder="z.B. Sommer-Aktion: 20% Rabatt auf 10er-Abos bis Ende Juli, neuer Kryosauna-Slot am Samstagmorgen, Erinnerung an die Mobile App"
            rows={3}
          />
          <div className="flex justify-end">
            <Button onClick={handleAiDraft} disabled={nlAiGenerating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="newsletter-ai-generate-btn">
              {nlAiGenerating ? '✨ Erstelle Vorlagen…' : '✨ E-Mail + WhatsApp mit KI erstellen'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">📰 Newsletter senden</CardTitle>
          <CardDescription>
            Senden Sie einen HTML-Newsletter an alle Kunden, die dem Empfang per Email bzw.
            WhatsApp zugestimmt haben.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* ── Block 1: Für neue Interessenten / Vorregistrierte ── */}
          <div className="flex items-center gap-2" data-testid="preset-group-interessenten">
            <span className="text-sm font-bold text-gray-800 whitespace-nowrap">🎉 Für neue Interessenten / Vorregistrierte</span>
            <span className="h-px flex-1 bg-gray-200" />
          </div>

          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="rapperswil-opening-preset">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-gray-900">🥂 Vorlage: Rapperswil-Jona Eröffnung</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Elegante Einladung zur Studio-Eröffnung inkl. Apéro-Infos, begrenzten Plätzen und Probetraining-Button – ohne Bilder, im StrongFit-Look (schwarz/gold).
                </p>
              </div>
              <Button
                type="button"
                onClick={() => {
                  setNewsletterSubject(RAPPERSWIL_OPENING_SUBJECT);
                  setNewsletterHtml(RAPPERSWIL_OPENING_HTML);
                  setShowPreview(true);
                  toast.success('Eröffnungs-Vorlage eingefügt – prüfe die Vorschau.');
                }}
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold shrink-0"
                data-testid="rapperswil-opening-fill-btn"
              >
                Vorlage einfügen
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="prereg-discount-preset">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-gray-900">🎉 Vorlage: 10% lebenslang (Vorregistrierte)</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Dankeschön-Mail für vorregistrierte Kunden mit dem dauerhaften 10%-Rabatt als Blickfang – im StrongFit-Look (schwarz/gold).
                </p>
              </div>
              <Button
                type="button"
                onClick={() => {
                  setNewsletterSubject(PREREG_DISCOUNT_SUBJECT);
                  setNewsletterHtml(PREREG_DISCOUNT_HTML);
                  setShowPreview(true);
                  toast.success('Vorregistrierungs-Vorlage (10% lebenslang) eingefügt – prüfe die Vorschau.');
                }}
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold shrink-0"
                data-testid="prereg-discount-fill-btn"
              >
                Vorlage einfügen
              </Button>
            </div>
          </div>

          {/* ── Block 2: Für Bestandskunden ── */}
          <div className="flex items-center gap-2 pt-2" data-testid="preset-group-bestandskunden">
            <span className="text-sm font-bold text-gray-800 whitespace-nowrap">💛 Für Bestandskunden</span>
            <span className="h-px flex-1 bg-gray-200" />
          </div>

          <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3" data-testid="security-notice-preset">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-gray-900">🔒 Vorlage: Sicherheitshinweis an alle Kunden</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Kurz &amp; knackig: melde dich sofort, falls ein Training ohne Grund abgesagt wird oder komische Mails/WhatsApps kommen – mit Direktlink an Alex ({ALEX_WA_NUMBER_DISPLAY}).
                </p>
              </div>
              <Button
                type="button"
                onClick={() => {
                  setNewsletterSubject(SECURITY_NOTICE_SUBJECT);
                  setNewsletterHtml(SECURITY_NOTICE_HTML);
                  setShowPreview(true);
                  toast.success('Sicherheitshinweis eingefügt – prüfe die Vorschau und sende an alle.');
                }}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold shrink-0"
                data-testid="security-notice-fill-btn"
              >
                Vorlage einfügen
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="release-notice-preset">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-gray-900">🚀 Vorlage: Neue StrongFit-App vorstellen</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Zeig deinen Kunden, was die App alles kann: Ernährung per Foto tracken, Termin-Warteliste, Kosten &amp; Abos im Griff, alles klar dokumentiert – im StrongFit-Look (schwarz/gold).
                </p>
              </div>
              <Button
                type="button"
                onClick={() => {
                  setNewsletterSubject(SOFTWARE_RELEASE_SUBJECT);
                  setNewsletterHtml(SOFTWARE_RELEASE_HTML);
                  setShowPreview(true);
                  toast.success('Release-Vorlage eingefügt – prüfe die Vorschau und sende an alle.');
                }}
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold shrink-0"
                data-testid="release-notice-fill-btn"
              >
                Vorlage einfügen
              </Button>
            </div>
          </div>


          <div className="space-y-2">
            <Label>Betreff *</Label>
            <Input
              value={newsletterSubject}
              onChange={(e) => setNewsletterSubject(e.target.value)}
              placeholder="z.B. Neuigkeiten im StrongFit Studio"
            />
          </div>

          <div className="space-y-2">
            <Label>Inhalt (HTML)</Label>
            <textarea
              value={newsletterHtml}
              onChange={(e) => setNewsletterHtml(e.target.value)}
              className="w-full min-h-[220px] border rounded-md p-2 font-mono text-sm"
              placeholder="<h1>Hallo!</h1><p>Hier sind unsere Neuigkeiten...</p>"
            />
            <p className="text-xs text-gray-500">
              Sie können einfachen HTML-Text verwenden (Überschriften, Absätze, Links).
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={newsletterSendEmail}
                onCheckedChange={setNewsletterSendEmail}
              />
              <span className="text-sm">Per Email senden</span>
            </div>
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900" data-testid="newsletter-recipient-preview">
            {recipientPreview ? (
              <>📬 Dieser Newsletter geht an <strong>{recipientPreview.email_recipients} Kunden per E-Mail</strong> (von {recipientPreview.total_customers} Kunden gesamt – nur mit aktivem E-Mail-Opt-In). Der Versand erfolgt <strong>sofort</strong> nach dem Klick.</>
            ) : (
              'Hinweis: Der Newsletter wird nur an Kunden mit aktiviertem E-Mail-Opt-In und gültiger E-Mail-Adresse gesendet. Versand erfolgt sofort.'
            )}
          </div>

          <div className="p-3 bg-green-50 border border-green-200 rounded-md text-xs text-green-900" data-testid="newsletter-unsubscribe-hint">
            ✅ <strong>Automatisch dabei:</strong> Jeder Newsletter enthält am Ende einen <strong>Abmelde-Link</strong> (1-Klick, DSGVO/revDSG-konform) sowie den Hinweis „WhatsApp-Newsletter mit <strong>STOPP</strong> abbestellen". Du musst nichts selbst einfügen.
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
              data-testid="newsletter-preview-toggle"
            >
              {showPreview ? '👁️ Vorschau ausblenden' : '👁️ Vorschau anzeigen'}
            </Button>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={handleTestSend} disabled={testSending} data-testid="newsletter-test-send-btn">
                {testSending ? 'Sende…' : '📧 Test an mich senden'}
              </Button>
              <input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                className="border rounded-md px-2 py-1.5 text-sm"
                data-testid="newsletter-schedule-input"
              />
              <Button variant="outline" onClick={handleSchedule} disabled={scheduling} data-testid="newsletter-schedule-btn">
                {scheduling ? 'Plane…' : '🕐 Planen'}
              </Button>
              <Button onClick={handleSendNewsletter} disabled={sendingNewsletter} data-testid="newsletter-send-now-btn">
                {sendingNewsletter ? 'Versende...' : 'Newsletter jetzt senden'}
              </Button>
            </div>
          </div>

          {showPreview && newsletterHtml.trim() && (
            <div className="rounded-lg overflow-hidden border" data-testid="newsletter-preview">
              <div className="px-4 py-2 bg-gray-100 text-xs text-gray-600 border-b">
                Vorschau (so sieht der Inhalt im schwarzen E-Mail-Design aus) – Betreff: <strong>{newsletterSubject || '(kein Betreff)'}</strong>
              </div>
              <div
                style={{ background: '#0a0a0a', color: '#ffffff', padding: '24px', fontFamily: 'Arial, sans-serif' }}
                dangerouslySetInnerHTML={{ __html: newsletterHtml }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {scheduledList.filter(s => s.status === 'pending').length > 0 && (
        <Card data-testid="scheduled-newsletters-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">🕐 Geplante Newsletter</CardTitle>
            <CardDescription>Diese Newsletter werden automatisch zum geplanten Zeitpunkt versendet.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {scheduledList.filter(s => s.status === 'pending').map((s) => (
              <div key={s.id} className="flex items-center justify-between p-3 border rounded-lg bg-amber-50 border-amber-200">
                <div>
                  <div className="text-sm font-medium">{s.subject}</div>
                  <div className="text-xs text-gray-600">📅 {new Date(s.scheduled_at).toLocaleString('de-CH')}</div>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleCancelScheduled(s.id)} data-testid={`cancel-scheduled-${s.id}`}>
                  Stornieren
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* WhatsApp-Newsletter (Template) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">💬 WhatsApp-Newsletter (Template)</CardTitle>
          <CardDescription>
            Senden Sie einen WhatsApp-Newsletter über eine genehmigte Template-Nachricht.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Template-Name *</Label>
            {loadingWaTemplates ? (
              <p className="text-xs text-gray-500">Lade WhatsApp-Templates...</p>
            ) : waTemplates.length > 0 ? (
              <select
                className="w-full border rounded-md p-2 text-sm bg-white"
                value={waTemplateName}
                onChange={(e) => {
                  const value = e.target.value;
                  setWaTemplateName(value);
                  const tpl = waTemplates.find((t) => t.name === value);
                  if (tpl) {
                    if (tpl.language) {
                      setWaLanguageCode(tpl.language);
                    }
                    // Anzahl Body-Parameter aus der Template-Definition ableiten
                    const bodyComponent =
                      tpl.components?.find((c) => c.type === 'BODY') || null;
                    const text = bodyComponent?.text || '';
                    const matches = text.match(/{{\d+}}/g) || [];
                    setWaRequiredParams(matches.length);
                    // Beispiel-Werte aus Template-Beispiel oder Testdaten vorbelegen
                    const example = bodyComponent?.example?.body_text?.[0] || [];
                    setWaVarValues(
                      Array.from({ length: matches.length }, (_, i) =>
                        example[i] || (i === 0 ? 'Alex' : 'Beispiel')
                      )
                    );
                  } else {
                    setWaRequiredParams(0);
                    setWaVarValues([]);
                  }
                }}
              >
                <option value="">Bitte Template auswählen</option>
                {waTemplates.map((tpl) => (
                  <option key={tpl.id} value={tpl.name}>
                    {tpl.name} {tpl.status ? `(${tpl.status})` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                value={waTemplateName}
                onChange={(e) => setWaTemplateName(e.target.value)}
                placeholder="z.B. newsletter_update"
              />
            )}
            <p className="text-xs text-gray-500">
              Es werden alle bei Meta hinterlegten Templates (Status APPROVED) geladen. Falls die Liste leer ist,
              können Sie den Namen manuell eingeben.
            </p>
          </div>

          {/* Sprache wird automatisch aus dem ausgewählten Template übernommen */}

          <p className="text-xs text-gray-500">
            Dieses Template verwendet automatisch den Vornamen des Kunden als Platzhalter (&#123;&#123;1&#125;&#125;).
          </p>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900" data-testid="wa-newsletter-recipient-preview">
            📱 Geht an <strong>{recipientPreview ? recipientPreview.whatsapp_recipients : '…'} Kunden per WhatsApp</strong> (nur mit aktivem WhatsApp-Opt-In). Versand erfolgt sofort.
          </div>

          {waRequiredParams > 0 && (
            <div className="space-y-2" data-testid="wa-var-inputs">
              <Label className="text-xs">Platzhalter-Werte (für Vorschau &amp; Versand)</Label>
              {Array.from({ length: waRequiredParams }).map((_, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-500 w-12">{`{{${i + 1}}}`}</span>
                  <Input
                    value={waVarValues[i] || ''}
                    onChange={(e) => {
                      const next = [...waVarValues];
                      next[i] = e.target.value;
                      setWaVarValues(next);
                    }}
                    placeholder={i === 0 ? 'z.B. Vorname' : 'Wert'}
                    className="text-sm"
                    data-testid={`wa-var-input-${i}`}
                  />
                </div>
              ))}
              <p className="text-[11px] text-gray-500">Beim echten Versand wird {`{{1}}`} automatisch durch den Vornamen jedes Kunden ersetzt.</p>
            </div>
          )}

          {(() => {
            const tpl = waTemplates.find((t) => t.name === waTemplateName);
            const body = tpl?.components?.find((c) => c.type === 'BODY')?.text || '';
            if (!waTemplateName || !body) return null;
            return (
              <div data-testid="wa-message-preview">
                <Label className="text-xs mb-1 block">📱 Nachrichten-Vorschau (so kommt sie beim Kunden an)</Label>
                <div style={{ background: '#0b141a', padding: '14px', borderRadius: '10px' }}>
                  <div style={{ background: '#005c4b', color: '#e9edef', padding: '8px 10px', borderRadius: '8px', maxWidth: '85%', marginLeft: 'auto', whiteSpace: 'pre-wrap', fontSize: '13px', lineHeight: 1.5 }}>
                    {fillWaVars(body, waVarValues)}
                  </div>
                </div>
              </div>
            );
          })()}

          {waAiDraft && (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 space-y-2" data-testid="wa-ai-draft">
              <p className="text-sm font-semibold text-emerald-900">✨ KI-Entwurf für WhatsApp-Vorlage</p>
              <div style={{ background: '#0b141a', padding: '12px', borderRadius: '8px' }}>
                <div style={{ background: '#005c4b', color: '#e9edef', padding: '8px 10px', borderRadius: '8px', maxWidth: '85%', whiteSpace: 'pre-wrap', fontSize: '13px', lineHeight: 1.5 }}>
                  {fillWaVars(waAiDraft.body_text, waAiDraft.example_values || [])}
                </div>
              </div>
              <p className="text-[11px] text-emerald-800">
                Name: <code>{waAiDraft.suggested_name}</code> · Sprache: {waAiDraft.language} · Kategorie: {waAiDraft.category}
              </p>
              <div className="flex gap-2">
                <Button size="sm" disabled={creatingWaTemplate} onClick={handleCreateWaTemplate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-ai-create-btn">
                  {creatingWaTemplate ? 'Wird angelegt…' : 'Bei Meta anlegen'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setWaAiDraft(null)} data-testid="wa-ai-discard-btn">Verwerfen</Button>
              </div>
              <p className="text-[11px] text-emerald-700">Legt die Vorlage nur bei Meta zur Genehmigung an – es wird nichts an Kunden gesendet. Nach Freigabe erscheint sie oben in der Auswahl.</p>
            </div>
          )}

          <div className="text-xs">
            <a href="/whatsapp/templates" className="text-indigo-600 hover:underline font-medium" data-testid="wa-manage-templates-link">
              → Alle WhatsApp-Vorlagen verwalten (anlegen, Vorschau, löschen)
            </a>
          </div>


          <div className="flex justify-end">
            <Button
              variant="outline"
              disabled={sendingWhatsapp}
              onClick={async () => {
                if (!waTemplateName.trim()) {
                  toast.error('Bitte einen Template-Namen angeben');
                  return;
                }

                try {
                  setSendingWhatsapp(true);
                  const token = localStorage.getItem('token');

                  const bodyParamsArr = waRequiredParams > 0
                    ? (waVarValues || []).slice(0, waRequiredParams)
                    : [];

                  const res = await axios.post(
                    `${API}/newsletters/whatsapp/send-template`,
                    {
                      template_name: waTemplateName,
                      language_code: waLanguageCode || 'de',
                      body_params: bodyParamsArr.length > 0 ? bodyParamsArr : undefined,
                    },
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );

                  toast.success(
                    `WhatsApp-Newsletter gestartet (WhatsApp: ${res.data.sent ?? 0})`
                  );

                  setWaTemplateName('');
                  setWaLanguageCode('de');
                  setWaBodyParams('');

                  // Verlauf nach Versand aktualisieren
                  fetchLogs();
                } catch (error) {
                  console.error('Fehler beim WhatsApp-Newsletter-Versand', error);
                  toast.error(
                    error.response?.data?.detail || 'Fehler beim WhatsApp-Newsletter-Versand'
                  );
                } finally {
                  setSendingWhatsapp(false);
                }
              }}
            >
              {sendingWhatsapp ? 'Versende...' : 'WhatsApp-Newsletter senden'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="newsletter-history-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">📊 Newsletter-Verlauf</CardTitle>
          <CardDescription>
            Hier siehst du, was wann an wie viele Kunden gesendet wurde (E-Mail / WhatsApp).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <p className="text-sm text-gray-500">Lade Verlauf...</p>
          ) : logs.length === 0 ? (
            <p className="text-sm text-gray-500">Bisher wurden noch keine Newsletter versendet.</p>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 border rounded-lg bg-gray-50"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">
                        {log.created_at
                          ? new Date(log.created_at).toLocaleString('de-CH')
                          : ''}
                      </span>
                      <Badge variant="outline">{log.channels?.join(', ') || 'email'}</Badge>
                    </div>
                    <div className="text-sm font-medium">
                      {log.subject || '(kein Betreff)'}
                    </div>
                  </div>
                  <div className="mt-2 sm:mt-0 flex gap-3 text-xs text-gray-600">
                    <span>
                      <strong>Email:</strong> {log.sent_email ?? 0}
                    </span>
                    <span>
                      <strong>WhatsApp:</strong> {log.sent_whatsapp ?? 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="push" className="space-y-6 mt-4">
          <PushBroadcastPanel />
          <BroadcastHistory />
          <PushABTests />
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <UpsellPushSettings />
          </div>
        </TabsContent>

        <TabsContent value="automations" className="space-y-6 mt-4">
      {/* Win-Back Automation */}
      <WinBackFlow />

      {/* Iter 310: Trial-Follow-Up Conversion-Tracking */}
      <TrialFollowupConversionWidget />

      {/* Push-Automatisierungen */}
      <PushAutomations />

      {/* Smart Coach – kontextbezogene Push-Nachrichten */}
      <SmartCoachSettings />

      {/* Push-Reminder-Konfiguration (Ruhezeiten, 60min/24h/Inaktivität) */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <PushReminderConfig />
      </div>

      {/* App-Push-Benachrichtigungen (Templates + Toggles) */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <MobileNotificationTemplates />
      </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6 mt-4">
      {/* Push-Analytics & KI-Empfehlungen */}
      <PushAnalytics />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CampaignsPage;
